import * as Radix from '@radix-ui/react-dropdown-menu'
import { ChevronRight, KeyRound, LogOut, Monitor, Moon, Sun, UserRound } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { Avatar } from '../ui/Avatar.js'
import { Text } from '../ui/Typography.js'
import { ToggleGroup } from '../ui/ToggleGroup.js'
import { usePortalTarget } from '../ui/PortalTarget.js'
import type { ThemeChoice } from '../../lib/theme.js'

export interface ProfileMenuProps {
  user: { fullName: string; roleName: string }
  theme: ThemeChoice
  onTheme: (next: ThemeChoice) => void
  onOpenProfile?: (() => void) | undefined
  onChangePassword?: (() => void) | undefined
  onSignOut: () => void
  trigger: React.ReactNode
}

/**
 * One row: an icon, a label, and a chevron that follows the cursor.
 *
 * The icons were in 32px tinted tiles, which is a list of buttons rather
 * than a menu and cost 60px of height across three rows for no information.
 */
function Item({
  icon,
  label,
  onSelect,
  destructive,
}: {
  icon: React.ReactNode
  label: string
  onSelect: () => void
  destructive?: boolean
}) {
  return (
    <Radix.Item
      onSelect={onSelect}
      className={cn(
        'group flex h-9 cursor-pointer items-center gap-3 rounded-md px-2.5 text-body outline-none',
        'data-[highlighted]:bg-sunk transition-colors',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        destructive ? 'text-error' : 'text-ink',
      )}
    >
      <span className={destructive ? 'text-error' : 'text-ink-muted'}>{icon}</span>
      <span className="flex-1">{label}</span>
      {!destructive && (
        <ChevronRight className="text-ink-muted opacity-0 transition-opacity group-data-[highlighted]:opacity-100" />
      )}
    </Radix.Item>
  )
}

/**
 * The person, and what they can do about being the person.
 *
 * Built rather than assembled out of `DropdownMenu`, because a profile menu
 * is not a list of verbs. It was one: *Sign out*, with the appearance control
 * bolted above it — which is a settings menu that happens to hang off a face.
 *
 * What it says first is **who you are**: the portrait at a size worth looking
 * at, the name, the address the platform writes to, and the role as a badge —
 * because "Operations" is the answer to what this person may do, and it is
 * the thing a colleague asks about. Then the appearance, then the two forms,
 * then the way out in the error ink with a rule above it.
 *
 * The chevron appears on the row under the cursor rather than on all of them:
 * three permanent arrows is decoration, one that follows you is a pointer.
 */
export function ProfileMenu({
  user,
  theme,
  onTheme,
  onOpenProfile,
  onChangePassword,
  onSignOut,
  trigger,
}: ProfileMenuProps) {
  /* See `PortalTarget`. Not needed from the rail, but the rule is the rule. */
  const container = usePortalTarget()
  return (
    <Radix.Root>
      <Radix.Trigger asChild>{trigger}</Radix.Trigger>
      <Radix.Portal {...(container ? { container } : {})}>
        <Radix.Content
          align="start"
          side="top"
          sideOffset={8}
          className="border-hairline bg-surface z-50 w-60 overflow-hidden rounded-lg border shadow-e2"
        >
          {/*
            The head sits on the sunk ground rather than the surface, so the
            panel reads as a card with a lid rather than a list with a bold
            first row. 40px portrait and the role as a quiet line rather than
            a badge — a badge in a 240px panel is a second shape competing
            with the name beside it.
          */}
          <div className="bg-sunk border-hairline flex items-center gap-3 border-b px-3 py-3">
            <Avatar name={user.fullName} size="md" tone="teal" />
            <div className="flex min-w-0 flex-1 flex-col">
              <Text as="span" variant="body-strong" className="truncate">
                {user.fullName}
              </Text>
              <Text as="span" variant="caption" className="truncate">
                {user.roleName}
              </Text>
            </div>
          </div>

          <div className="border-hairline flex items-center justify-between gap-2 border-b px-3 py-2">
            <Text as="span" variant="caption">
              Appearance
            </Text>
            <ToggleGroup
              className="w-[108px]"
              type="single"
              variant="segmented"
              value={theme}
              onValueChange={(v) => v && onTheme(v as ThemeChoice)}
              /* `onKeyDown` stops the menu reading the arrow keys the group
                 wants, and nothing here closes it — so light and dark can be
                 compared without reopening the menu twice. */
              onKeyDown={(e) => e.stopPropagation()}
              options={[
                { value: 'light', label: <Sun />, srLabel: 'Light' },
                { value: 'dark', label: <Moon />, srLabel: 'Dark' },
                { value: 'system', label: <Monitor />, srLabel: 'Match my computer' },
              ]}
            />
          </div>

          <div className="p-1.5">
            {onOpenProfile && (
              <Item icon={<UserRound />} label="Edit profile" onSelect={onOpenProfile} />
            )}
            {onChangePassword && (
              <Item icon={<KeyRound />} label="Change password" onSelect={onChangePassword} />
            )}
            <Radix.Separator className="bg-hairline my-1.5 h-px" />
            <Item icon={<LogOut />} label="Sign out" onSelect={onSignOut} destructive />
          </div>
        </Radix.Content>
      </Radix.Portal>
    </Radix.Root>
  )
}
